import { NmLoader } from '../NmLoader';
import { redirectToCloudIntegration } from '../../utils/cloudIntegrationRedirect';
import { redirectToGarmin } from '../../utils/garminRedirect';
import {StravaConnectionExports} from '../StravaConnectionExports';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../NmButton';
import {
  athleteSettingsApi,
  CLOUD_INTEGRATION_PROVIDERS,
  type CloudIntegrationProvider,
} from '../../services/athleteSettingsApi';
import { GarminConnectSheet } from './GarminConnectSheet';
import {
  hasSeenGarminConnectSheet,
  markGarminConnectSheetSeen,
} from '../../utils/garminConnectSheetStorage';
import { parseCloudOAuthMessage } from '../../utils/cloudIntegrationOAuth';
import ConnectionBrandLogo from './ConnectionBrandLogo';

export type ConnectionsPanelVariant = 'settings' | 'onboarding';

export interface ConnectionsPanelProps {
  preview?: boolean;
  athleteId: number;
  /** Initial connected_apps from parent; refreshed after connect/disconnect. */
  connectedApps?: Record<string, boolean>;
  variant?: ConnectionsPanelVariant;
  onStatusChange?: (apps: Record<string, boolean>) => void;
  onMessage?: (message: string) => void;
}

type AppKey = 'garmin' | 'polar' | 'wahoo' | 'coros' | 'suunto' | 'apple' | 'samsung' | 'whoop' | 'igps' | 'strava';

const APP_KEYS: AppKey[] = ['garmin', 'polar', 'wahoo', 'coros', 'suunto', 'whoop', 'apple', 'samsung', 'igps', 'strava'];
const CLOUD_PROVIDER_SET = new Set<string>(CLOUD_INTEGRATION_PROVIDERS);

/** Checks Garmin link via athlete-details; falls back to get-athletes list. */
export async function verifyGarminConnected(athleteId: number): Promise<boolean> {
  try {
    const [detailsRes, athletesRes] = await Promise.all([
      athleteSettingsApi.getAthleteDetails(athleteId),
      athleteSettingsApi.getAthletes(),
    ]);
    if (detailsRes.data.data.details?.connected_apps?.garmin) return true;
    const fromList = athletesRes.data.data.athletes?.find((a) => a.id === athleteId);
    return !!fromList?.connected_apps?.garmin;
  } catch {
    return false;
  }
}

export async function verifyCloudConnected(
  athleteId: number,
  provider: CloudIntegrationProvider,
): Promise<boolean> {
  try {
    const res = await athleteSettingsApi.getCloudIntegrationStatus(provider, athleteId);
    return res.data.data.connected;
  } catch {
    return false;
  }
}

async function loadConnectedApps(athleteId: number): Promise<Record<string, boolean>> {
  try {
    const res = await athleteSettingsApi.getAthleteDetails(athleteId);
    return res.data.data.details?.connected_apps ?? {};
  } catch {
    return {};
  }
}

/** Apps & devices grid — Garmin OAuth + coming-soon providers. */
export function ConnectionsPanel({
  preview = false,
  athleteId,
  connectedApps: connectedAppsProp,
  variant = 'settings',
  onStatusChange,
  onMessage,
}: ConnectionsPanelProps) {
  const { t } = useTranslation();
  const [apps, setApps] = useState<Record<string, boolean>>(connectedAppsProp ?? {});
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState<CloudIntegrationProvider | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<CloudIntegrationProvider | null>(null);
  const [loadingApps, setLoadingApps] = useState(!connectedAppsProp);
  const [connectSheetOpen, setConnectSheetOpen] = useState(false);
  const [helpSheetOpen, setHelpSheetOpen] = useState(false);
  const [stravaPermissionOwner, setStravaPermissionOwner] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStravaPermissionOwner(null);
    if (apps.strava && connectingProvider !== 'strava') {
      void athleteSettingsApi.getCloudIntegrationStatus('strava', athleteId).then(res => {
        const status = res.data.data;
        if (!cancelled && status.connected && !status.scopes?.includes('activity:write')) {
          setStravaPermissionOwner(athleteId);
        }
      }).catch(() => { /* Keep the normal connection controls if status is unavailable. */ });
    }
    return () => { cancelled = true; };
  }, [athleteId, apps.strava, connectingProvider]);
  const oauthOrigins = useRef<Partial<Record<CloudIntegrationProvider, string>>>({});
  const oauthPopups = useRef<Partial<Record<CloudIntegrationProvider, Window | null>>>({});
  const oauthTimers = useRef<Partial<Record<CloudIntegrationProvider, number>>>({});

  const notifyGarminConnected = useCallback(() => {
    onMessage?.(t('athleteSettings.garminConnectedSync'));
  }, [onMessage, t]);

  const refreshApps = useCallback(async () => {
    const next = await loadConnectedApps(athleteId);
    setApps(next);
    onStatusChange?.(next);
    return next;
  }, [athleteId, onStatusChange]);

  const finishCloudConnection = useCallback(async (provider: CloudIntegrationProvider) => {
    const connected = await verifyCloudConnected(athleteId, provider);
    if (!connected) return false;
    await refreshApps();
    setConnectingProvider((current) => current === provider ? null : current);
    onMessage?.(`${t(`athleteSettings.connections.apps.${provider}`)}: ${t('athleteSettings.connections.connected')}`);
    return true;
  }, [athleteId, onMessage, refreshApps, t]);

  useEffect(() => {
    if (connectedAppsProp) {
      setApps(connectedAppsProp);
      setLoadingApps(false);
      return;
    }
    let cancelled = false;
    void loadConnectedApps(athleteId).then((next) => {
      if (!cancelled) {
        setApps(next);
        setLoadingApps(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [athleteId, connectedAppsProp]);

  useEffect(() => {
    const electronGarmin = (window as Window & {
      electronAPI?: {
        garmin?: {
          onOAuthCallback?: (
            cb: (result: { success: boolean; error?: string }) => void,
          ) => (() => void) | void;
        };
      };
    }).electronAPI?.garmin;

    if (electronGarmin?.onOAuthCallback) {
      const unsub = electronGarmin.onOAuthCallback(async ({ success, error }) => {
        setConnecting(false);
        if (success) {
          const connected = await verifyGarminConnected(athleteId);
          if (connected) {
            await refreshApps();
            notifyGarminConnected();
          } else {
            onMessage?.(t('athleteSettings.error.garminIncomplete'));
          }
        } else if (error && error !== 'cancelled') {
          onMessage?.(error);
        }
      });
      return () => {
        unsub?.();
      };
    }

    function onMessageEvent(event: MessageEvent) {
      const cloudResult = parseCloudOAuthMessage(event.origin, event.data, oauthOrigins.current);
      if (cloudResult) {
        const expectedPopup = oauthPopups.current[cloudResult.provider];
        if (expectedPopup && event.source !== expectedPopup) return;
        const timer = oauthTimers.current[cloudResult.provider];
        if (timer) window.clearInterval(timer);
        setConnectingProvider((current) => current === cloudResult.provider ? null : current);
        if (cloudResult.success) {
          void finishCloudConnection(cloudResult.provider);
        } else if (cloudResult.message && cloudResult.message !== 'cancelled') {
          onMessage?.(cloudResult.message);
        }
        return;
      }
      const data = event.data as { type?: string; success?: boolean; error?: string };
      if (event.origin !== window.location.origin) return;
      if (data?.type !== 'vo2-garmin-oauth') return;
      setConnecting(false);
      if (data.success) {
        void verifyGarminConnected(athleteId).then(async (connected) => {
          if (connected) {
            await refreshApps();
            notifyGarminConnected();
          } else {
            onMessage?.(t('athleteSettings.error.garminIncomplete'));
          }
        });
      } else if (data.error && data.error !== 'cancelled') {
        onMessage?.(data.error);
      }
    }
    window.addEventListener('message', onMessageEvent);
    return () => window.removeEventListener('message', onMessageEvent);
  }, [athleteId, finishCloudConnection, notifyGarminConnected, onMessage, refreshApps, t]);

  useEffect(() => () => {
    Object.values(oauthTimers.current).forEach((timer) => {
      if (timer) window.clearInterval(timer);
    });
  }, []);

  const runGarminOAuth = useCallback(async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      // PWA uses the current browsing context, including Safari with popups blocked.
      await redirectToGarmin(athleteId);
    } catch (e: unknown) {
      onMessage?.(e instanceof Error ? e.message : t('athleteSettings.error.garminFailed'));
      setConnecting(false);
    }
  }, [athleteId, connecting, onMessage, t]);

  const runCloudOAuth = useCallback(async (provider: CloudIntegrationProvider) => {
    if (connectingProvider) return;
    setConnectingProvider(provider);
    try {
      await redirectToCloudIntegration(provider, athleteId);
    } catch (e: unknown) {
      onMessage?.(e instanceof Error ? e.message : `${provider} connection failed`);
      setConnectingProvider((current) => current === provider ? null : current);
    }
  }, [athleteId, connectingProvider, onMessage]);

  function shouldShowConnectSheet(): boolean {
    if (variant === 'onboarding') return true;
    return !hasSeenGarminConnectSheet();
  }

  function handleGarminConnectClick() {
    if (shouldShowConnectSheet()) {
      setConnectSheetOpen(true);
      return;
    }
    void runGarminOAuth();
  }

  function handleConnectSheetContinue() {
    markGarminConnectSheetSeen();
    setConnectSheetOpen(false);
    void runGarminOAuth();
  }

  async function disconnectGarmin() {
    if (disconnecting) return;
    const confirmed = window.confirm(t('athleteSettings.connections.disconnectConfirm'));
    if (!confirmed) return;

    setDisconnecting(true);
    try {
      await athleteSettingsApi.disconnectGarmin(athleteId);
      await refreshApps();
      onMessage?.(t('athleteSettings.garminDisconnected'));
    } catch (e: unknown) {
      onMessage?.(e instanceof Error ? e.message : t('athleteSettings.error.garminDisconnectFailed'));
    } finally {
      setDisconnecting(false);
    }
  }

  async function disconnectCloudProvider(provider: CloudIntegrationProvider) {
    if (disconnectingProvider) return;
    const confirmed = window.confirm(t(provider === 'strava' ? 'athleteSettings.connections.stravaDisconnect' : 'athleteSettings.connections.disconnectConfirm'));
    if (!confirmed) return;

    setDisconnectingProvider(provider);
    try {
      await athleteSettingsApi.disconnectCloudIntegration(provider, athleteId);
      await refreshApps();
      onMessage?.(`${t(`athleteSettings.connections.apps.${provider}`)}: ${t('athleteSettings.connections.disconnect')}`);
    } catch (e: unknown) {
      onMessage?.(e instanceof Error ? e.message : `${provider} disconnect failed`);
    } finally {
      setDisconnectingProvider(null);
    }
  }

  const introKey =
    variant === 'onboarding'
      ? 'athleteSettings.connections.onboardingIntro'
      : 'athleteSettings.connections.intro';

  return (
    <div className={`connections-panel connections-panel--${variant}`} aria-busy={loadingApps}>
      {variant !== 'onboarding' && (
        <p className="connections-panel__intro">{t(introKey)}</p>
      )}

      {loadingApps ? (
        <p className="connections-panel__loading"><NmLoader size="sm" inline label={t('common.loading')} /></p>
      ) : (
        <div className="connections-panel__list">
          {APP_KEYS.map((key) => {
            const isCloudProvider = CLOUD_PROVIDER_SET.has(key);
            const supported = key === 'garmin' || isCloudProvider;
            const connected = !!apps[key];
            const isGarmin = key === 'garmin';
            const cloudProvider = isCloudProvider ? key as CloudIntegrationProvider : null;

            return (
              <div
                key={key}
                role="group"
                aria-label={t(`athleteSettings.connections.apps.${key}`)}
                className={`connections-panel__card${isGarmin ? ' connections-panel__card--featured' : ''}${connected ? ' connections-panel__card--connected' : ''}`}
              >
                <div className="connections-panel__card-row">
                  <div className="connections-panel__card-main">
                    <span className="connections-panel__icon" data-brand={key} aria-hidden>
                      <ConnectionBrandLogo brand={key} />
                    </span>
                    <div className="connections-panel__copy">
                      <span className="connections-panel__name">
                        {t(`athleteSettings.connections.apps.${key}`)}
                      </span>
                      {isGarmin && variant !== 'onboarding' && !connected && (
                        <span className="connections-panel__desc">
                          {t('athleteSettings.connections.garminDesc')}
                        </span>
                      )}
                      {!isGarmin && supported && (
                        <span className="connections-panel__desc">
                          {connected
                            ? t('athleteSettings.connections.syncActive', 'Sync is active')
                            : supported
                              ? t('athleteSettings.connections.syncHint', 'Connect to sync workouts')
                              : t('athleteSettings.connections.comingSoonHint', 'Coming soon')}
                        </span>
                      )}
                      {isGarmin && connected && (
                        <button
                          type="button"
                          className="connections-panel__help-link"
                          onClick={() => setHelpSheetOpen(true)}
                        >
                          {t('athleteSettings.connections.garminHelp.link')}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="connections-panel__actions">
                    {connected ? (
                      <>
                        <span className="connections-panel__badge connections-panel__badge--ok">
                          {t('athleteSettings.connections.connected')}
                        </span>
                        {key === 'strava' && stravaPermissionOwner === athleteId && <NmButton variant="ghost" size="sm" disabled={connectingProvider !== null} onClick={() => void runCloudOAuth('strava')}>{t('stravaExport.permissions')}</NmButton>}
                        {supported && (
                          <NmButton
                            variant="ghost"
                            size="sm"
                            className="connections-panel__btn connections-panel__btn--danger"
                            aria-label={`${t('athleteSettings.connections.disconnect')} · ${t(`athleteSettings.connections.apps.${key}`)}`}
                            disabled={preview || (isGarmin ? disconnecting : disconnectingProvider !== null)}
                            onClick={() => void (isGarmin
                              ? disconnectGarmin()
                              : disconnectCloudProvider(cloudProvider!))}
                          >
                            {(isGarmin ? disconnecting : disconnectingProvider === cloudProvider)
                              ? t('athleteSettings.disconnecting')
                              : t('athleteSettings.connections.disconnect')}
                          </NmButton>
                        )}
                      </>
                    ) : supported ? (
                      <NmButton
                        variant="primary"
                        size="sm"
                        className="connections-panel__btn"
                        aria-label={`${t('athleteSettings.connections.connect')} · ${t(`athleteSettings.connections.apps.${key}`)}`}
                        disabled={preview || (isGarmin ? connecting : connectingProvider !== null)}
                        onClick={isGarmin
                          ? handleGarminConnectClick
                          : () => void runCloudOAuth(cloudProvider!)}
                      >
                        {(isGarmin ? connecting : connectingProvider === cloudProvider)
                          ? t('athleteSettings.connecting')
                          : t('athleteSettings.connections.connect')}
                      </NmButton>
                    ) : (
                      <span className="connections-panel__badge connections-panel__badge--soon">
                        {t('athleteSettings.connections.comingSoon')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {variant === 'settings' && apps.strava && <StravaConnectionExports key={athleteId} userId={athleteId} />}

      <GarminConnectSheet
        open={connectSheetOpen}
        mode="connect"
        showLater
        onClose={() => setConnectSheetOpen(false)}
        onContinue={handleConnectSheetContinue}
      />
      <GarminConnectSheet
        open={helpSheetOpen}
        mode="help"
        onClose={() => setHelpSheetOpen(false)}
      />
    </div>
  );
}

